import { Box } from '@mui/material';
import { RiMicLine, RiStopCircleFill } from '@remixicon/react';
import { useEffect } from 'react';
import Hint from 'src/components/atoms/Hint';
import { AgButton, AgText } from 'src/components/ui';
import useAiVoiceTranscription from '../hooks/useAiVoiceTranscription';
import { useAiChatStore } from '../store/aiChat.store';
import EqualizerIconAnimated from './EqualizerIconAnimated';

export default function AiChatVoiceRecognition({ enablePushToTalk = true }: { enablePushToTalk?: boolean }) {
  const saveTranscriptionTurn = useAiChatStore((state) => state.saveTranscriptionTurn);
  const endTranscription = useAiChatStore((state) => state.endTranscription);
  const getIsVoiceRecording = useAiChatStore((state) => state.getIsVoiceRecording);
  const { startRecording, stopRecording, isSpeakingRef, isVoiceRecording } = useAiVoiceTranscription({
    onTranscribe: (turn) => {
      saveTranscriptionTurn(turn);
    },
    onStopRecording: () => {
      endTranscription();
    },
  });

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key !== ' ') return;

      event.preventDefault();
      const isVoiceRecording = getIsVoiceRecording();

      if (isVoiceRecording) {
        stopRecording();
      } else {
        startRecording();
      }
    };

    if (enablePushToTalk) {
      window.addEventListener('keydown', handleKeyDown);
    }

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [enablePushToTalk, getIsVoiceRecording, startRecording, stopRecording]);

  if (!isVoiceRecording) {
    return (
      <Hint
        title={enablePushToTalk ? 'Press the spacebar to start/stop voice recording.' : undefined}
        placement="top"
        arrow
      >
        <AgButton variant="ghost" size="icon-sm" shape="circle" onPress={startRecording}>
          <RiMicLine size={22} />
        </AgButton>
      </Hint>
    );
  }

  return (
    <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
      <EqualizerIconAnimated playing={isSpeakingRef.current} speed={0.65} barWidth={2} gap={2} color="#9FA09C" />
      <AgText size="14" className="leading-[18px] text-[#9FA09C]">
        Listening...
      </AgText>
      <AgButton variant="ghost" size="icon-sm" shape="circle" onPress={stopRecording}>
        <RiStopCircleFill className="text-primary" />
      </AgButton>
    </Box>
  );
}
